import { isEarlier } from "./consolidationFieldHighlights.js";
import { breakReasons } from "./consolidationBreaks.js";

// Un tonnage livré positif déjà enregistré (Data Manager ou brouillon) vaut une saisie : la commande compte comme livrée.
export const commandStatus = order => {
  if (order.execution_excluded) return "excluded";
  const status = order.order_execution_status || (order.execution_order_status === "completed" ? "delivered" : "pending");
  return status === "pending" && Number(order.delivered_tons) > 0 ? "delivered" : status;
};
export const executionDecision = (row, order) => row.execution_decisions?.find(d => d.order_id === order.id);
export const selectedCommandStatus = (order, decision) => decision?.status === "not_delivered" ? "excluded"
  : decision?.status === "delivered" ? "delivered" : decision?.status === "pending" ? "pending" : commandStatus(order);

// Le tonnage livré dit le statut : positif = livrée, 0 = non livrée, vide = à saisir.
export const tonnageStatus = value => value === "" || value == null ? "pending"
  : Number(value) > 0 ? "delivered" : Number(value) === 0 ? "not_delivered" : null;

// À saisir : champ vide, 0 pas encore confirmé, ou choix « à saisir » enregistré (le quitter applique la règle).
export const tonnageMissing = (order, decision, quantity) => !order.separate_treatment
  && (selectedCommandStatus(order, decision) === "pending" || (selectedCommandStatus(order, decision) === "delivered"
    && (quantity(order, "delivered_tons") == null || quantity(order, "delivered_tons") === "")));

export const PM_AMBIGUOUS_REASON = "Plusieurs relevés validés livrent ce chargé PM : rouvrez celui qui ne l’a pas livré, mettez 0 dans « Réel (t) » de sa commande, puis validez.";

export function validationMissingValues(row, quantity) {
  const reasons = [];
  const manualOrder = row.manual_order_id && row.visits.flatMap(v => v.orders).find(o => o.id === row.manual_order_id);
  if (manualOrder && selectedCommandStatus(manualOrder, executionDecision(row, manualOrder)) === "excluded") return [];
  if (row.visits.some(v => v.kind === "delivery" && v.orders.some(o => !o.separate_treatment
    && selectedCommandStatus(o, executionDecision(row, o)) === "pending"))) reasons.push("Tonnage réel à saisir (0 si non livrée).");
  if (!row.service_start) reasons.push("Renseignez le début du service.");
  if (!row.service_end) reasons.push("Renseignez la fin du service.");
  if (!row.no_lunch && Boolean(row.lunch_start) !== Boolean(row.lunch_end)) reasons.push("Complétez les deux heures du dîner.");
  reasons.push(...breakReasons(row));
  const checked = new Set();
  for (const visit of row.visits) {
    if (visit.pm?.role === "source") {
      if (!visit.departure) reasons.push(`Départ de ${visit.label} à renseigner.`);
      if (isEarlier(visit.departure, visit.arrival, row.timezone)) reasons.push(`${visit.label} · Le départ précède l’arrivée enregistrée.`);
      // A waiting delivery no longer blocks: billing splits the PM minutes once the delivery is validated.
      if (visit.pm_delivery?.orders?.some(order => order.status === "ambiguous")) reasons.push(PM_AMBIGUOUS_REASON);
      continue;
    }
    if (!visitFullyExcluded(row, visit) && isEarlier(visit.departure, visit.arrival, row.timezone)) {
      reasons.push(`${visit.label} · Le départ précède l’arrivée enregistrée.`);
    }
    const delivered = visit.orders.filter(order => !order.separate_treatment && selectedCommandStatus(order, executionDecision(row, order)) === "delivered");
    for (const order of delivered) {
      if (checked.has(order.id)) continue;
      checked.add(order.id);
      if (orderReferenceMissing(row, order)) reasons.push(`Numéro de référence de la commande ${order.label} à renseigner.`);
      // Sheets derive loaded tons from the delivered quantity entered by the user.
      const value = quantity(order, "delivered_tons");
      if (value === "" || value == null) reasons.push(`${order.label} · Tonnage livré à renseigner (t).`);
      else if (!(Number(value) > 0)) reasons.push(`${order.label} · Tonnage livré : une valeur positive est requise pour une commande livrée.`);
    }
    if (delivered.length && visit.status !== "inherited" && !visit.departure) reasons.push(`Départ de ${visit.label} à renseigner.`);
  }
  return [...new Set(reasons)];
}

// Référence exigée à la livraison de la commande ; un chargement sans livraison attend le relevé de livraison.
export const orderReferenceMissing = (row, order) => !order.loading_reference && !order.separate_treatment
  && selectedCommandStatus(order, executionDecision(row, order)) === "delivered"
  && ["loading", "delivery"].every(kind => row.visits.some(visit => visit.kind === kind && visit.orders.some(item => item.id === order.id)));

export const visitFullyExcluded = (row, visit) => !visit.pm && !visit.orders.some(o => o.pm_loading) && visit.orders.length > 0
  && visit.orders.every(order => selectedCommandStatus(order, executionDecision(row, order)) === "excluded");

export function chooseExecution(row, order, status) {
  const decisions = (row.execution_decisions || []).filter(d => d.order_id !== order.id);
  const baseline = commandStatus(order) === "excluded" ? "not_delivered" : commandStatus(order);
  if (status !== baseline) decisions.push({ order_id: order.id, status });
  // Heures d'un arrêt entièrement non livré : gardées dans la saisie, masquées et non envoyées ; un tonnage positif les rend.
  return { ...row, execution_decisions: decisions };
}
const confirmed = order => !order?.execution_excluded && order?.execution_confirmation?.status === "confirmed";
const zero = value => value !== "" && value != null && Number(value) === 0;
export const hasConfirmedOperation = visit => visit.orders.some(confirmed);

// Match calculation_context for editable drafts; historical statuses stay frozen.
export function visitTimingStatus(row, visit, quantity) {
  if (row.status !== "draft" || visit.status === "inherited") return visit.status;
  if (visit.pm?.role === "source") return visit.arrival || visit.departure ? "performed" : "unset";
  const active = visit.orders.filter(order => selectedCommandStatus(order, executionDecision(row, order)) !== "excluded");
  const field = visit.kind === "loading" ? "loaded_tons" : "delivered_tons";
  if (!active.length || active.every(order => zero(quantity(order, field)))) return "not_performed";
  if (visit.arrival || visit.departure || active.some(order => executionDecision(row, order)?.status === "delivered"
    || order.execution_confirmation?.status === "confirmed" || Number(quantity(order, field)) > 0)) return "performed";
  return "unset";
}
